import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { rwApi, setRwToken, getRwToken, formatApiErr } from "@/lib/rwApi";
import { Download, LogOut, RefreshCw, Search, Users, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function RwDashboardPage() {
  const navigate = useNavigate();
  const [officer, setOfficer] = useState(null);
  const [items, setItems] = useState([]);
  const [rts, setRts] = useState([]);
  const [rtFilter, setRtFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await rwApi.get("/rw/me");
      setOfficer(data);
    } catch (err) {
      // unauthenticated
      setRwToken("");
      navigate("/rw", { replace: true });
    }
  }, [navigate]);

  const fetchVoters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (rtFilter) params.rt = rtFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await rwApi.get("/rw/voters", { params });
      setItems(data.items || []);
      setRts(data.rts || []);
    } catch (err) {
      toast.error(formatApiErr(err));
      if (err?.response?.status === 401) {
        setRwToken("");
        navigate("/rw", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [rtFilter, q, navigate]);

  useEffect(() => {
    if (!getRwToken()) {
      navigate("/rw", { replace: true });
      return;
    }
    fetchMe();
  }, [fetchMe, navigate]);

  useEffect(() => {
    if (officer) fetchVoters();
  }, [officer, rtFilter, fetchVoters]);

  // Search with small debounce
  useEffect(() => {
    if (!officer) return;
    const t = setTimeout(fetchVoters, 300);
    return () => clearTimeout(t);
  }, [q, fetchVoters, officer]);

  const logout = async () => {
    try { await rwApi.post("/rw/logout"); } catch { /* ignore */ }
    setRwToken("");
    toast.success("Anda telah keluar.");
    navigate("/rw", { replace: true });
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (rtFilter) params.rt = rtFilter;
      const res = await rwApi.get("/rw/voters/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const rtSuffix = rtFilter ? `-RT${rtFilter}` : "";
      a.download = `DPS-RW-${officer?.rw || "RW"}${rtSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel diunduh.");
    } catch (err) {
      toast.error(formatApiErr(err));
    } finally {
      setDownloading(false);
    }
  };

  const verifiedCount = useMemo(() => items.filter((v) => v.coklit_verified).length, [items]);

  if (!officer) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="rw-loading">
        Memuat...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rw-dashboard-page">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center flex-shrink-0">
              <Users className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold tracking-tight truncate">
                Ketua RW {officer.rw} · {officer.dusun}
              </div>
              <div className="text-xs text-slate-500 truncate" data-testid="rw-officer-nama">{officer.nama}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="flex-shrink-0"
            data-testid="rw-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Keluar</span>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <SummaryCard label="Total Pemilih" value={items.length} icon={<Users className="h-4 w-4" />} />
          <SummaryCard label="Sudah Dicoklit" value={verifiedCount} icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />} />
          <SummaryCard label="Jumlah RT" value={rts.length} icon={<MapPin className="h-4 w-4" />} />
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari nama..."
                className="pl-9 h-10"
                data-testid="rw-search-input"
              />
            </div>
            <select
              value={rtFilter}
              onChange={(e) => setRtFilter(e.target.value)}
              className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
              data-testid="rw-rt-filter"
            >
              <option value="">Semua RT</option>
              {rts.map((rt) => (
                <option key={rt} value={rt}>RT {rt}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchVoters}
              className="h-10"
              data-testid="rw-refresh-btn"
              title="Muat ulang data terbaru"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              onClick={downloadXlsx}
              disabled={downloading || items.length === 0}
              className="bg-emerald-700 hover:bg-emerald-800 h-10"
              data-testid="rw-download-xlsx"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {downloading ? "Mengunduh..." : "Unduh Excel"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Data pemilih real-time. NIK tidak ditampilkan demi melindungi privasi pemilih.
          </p>
        </div>

        {/* Voter table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rw-voter-table">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold w-12">No</th>
                  <th className="px-3 py-2 text-left font-semibold">Nama</th>
                  <th className="px-3 py-2 text-center font-semibold w-12">JK</th>
                  <th className="px-3 py-2 text-center font-semibold w-16">RT</th>
                  <th className="px-3 py-2 text-left font-semibold">Alamat</th>
                  <th className="px-3 py-2 text-center font-semibold w-24">TPS</th>
                  <th className="px-3 py-2 text-center font-semibold w-24">Coklit</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-500" data-testid="rw-empty-state">
                    Tidak ada data pemilih.
                  </td></tr>
                )}
                {loading && items.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                )}
                {items.map((v, i) => (
                  <tr key={v.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{v.nama}</td>
                    <td className="px-3 py-2 text-center">{v.jenis_kelamin === "L" ? "L" : "P"}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{v.rt || "-"}</td>
                    <td className="px-3 py-2 text-slate-700">{v.alamat || "-"}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{v.tps || "-"}</td>
                    <td className="px-3 py-2 text-center">
                      {v.coklit_verified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Ya
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Belum</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-2xl font-bold mt-1 text-slate-900">{value.toLocaleString("id-ID")}</div>
    </div>
  );
}
